import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import {
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { AutocompleteCDS } from '@sharedCR/coreDesignSystem/form';
import { AddBoxIcon, ArrowIcon, DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import AddConditionFormTaskType from 'app/pages/loanApplication/tabs/underwriting/tabs/loanConditions/AddConditionFormTaskType';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { FeatureFlag } from 'constants/featureFlags';

import { selectTaskTypeDynamicField } from 'store/system/selectors';
import { selectSlaGroups } from 'store/underwriting/selectors';

import { ConditionsTestIds } from './Conditions.testIds';
import { tableStyles } from './ConditionTasksTable.styles';

const disablePortalProp = { disablePortal: true };

const ConditionTasksTable = ({
  tasks,
  taskTypeOptions,
  form,
  setInForm,
  handleChange,
  isLoading,
  isBrokerType,
  validateField,
  validationErrors,
  onCreateNewForm,
  formName,
  isTaskAssignmentEnabled,
}) => {
  const [isTaskTypeFieldVisible, setIsTaskTypeFieldVisible] = useState(false);

  const slaGroups = useSelector(selectSlaGroups);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);

  const isBrokerTasksFeatureEnabled = useFeatureToggle(
    FeatureFlag.EnableBrokerConditionTaskAssignment,
  );
  const isBrokerTasksEnabled = isTaskAssignmentEnabled ?? isBrokerTasksFeatureEnabled;

  const sortedTasks = useMemo(() => _.sortBy(tasks, [task => _.toLower(task?.name)]), [tasks]);

  const [formattedTasks, setFormattedTasks] = useState([]);

  useEffect(() => {
    setFormattedTasks(sortedTasks);
  }, [sortedTasks]);

  const notSelectedTaskTypeOptions = useMemo(() => {
    const selectedTaskTypeIds = form.taskTypeIds.map(({ taskTypeId }) => taskTypeId);

    return taskTypeOptions.filter(({ taskTypeId }) => !selectedTaskTypeIds.includes(taskTypeId));
  }, [form.taskTypeIds, taskTypeOptions]);

  const onAddTaskType = useCallback(
    (e, value) => {
      if (isBrokerType && isBrokerTasksEnabled) {
        setInForm(`conditionTaskTypes`, { ...form.conditionTaskTypes, [value.taskTypeId]: value });
      }

      setInForm('taskTypeIds', [...form.taskTypeIds, value]);
      setInForm('taskType', {});
      setIsTaskTypeFieldVisible(false);
    },
    [form.conditionTaskTypes, form.taskTypeIds, isBrokerTasksEnabled, isBrokerType, setInForm],
  );

  const onDeleteTaskType = useCallback(
    e => {
      const { id } = e.currentTarget.dataset;

      setInForm(
        'taskTypeIds',
        form.taskTypeIds.filter(({ taskTypeId }) => taskTypeId !== id),
      );
    },
    [form.taskTypeIds, setInForm],
  );

  const onAddTaskTypeButton = useCallback(
    () => setIsTaskTypeFieldVisible(true),
    [setIsTaskTypeFieldVisible],
  );

  const onExpandButton = useCallback(
    e => {
      const { id } = e.currentTarget.dataset;
      const newTasks = formattedTasks.map(task => {
        if (task.taskTypeId === id) {
          return {
            ...task,
            isExpanded: !task.isExpanded,
          };
        }

        return task;
      });
      setFormattedTasks(newTasks);
    },
    [formattedTasks],
  );

  const categoryName = useCallback(option => option.categoryName, []);

  return (
    <Stack gap={2}>
      {isTaskTypeFieldVisible && (
        <AutocompleteCDS
          disableClearable
          dataTestIdInput={ConditionsTestIds.CONDITION_FORM_CONDITION_TASK_TYPE_AUTOCOMPLETE_INPUT}
          dataTestIdOption={ConditionsTestIds.CONDITION_FORM_CONDITION_TASK_TYPE_OPTION}
          disabled={isLoading}
          groupBy={categoryName}
          label="Associate Task Type"
          name="taskType"
          optionKey="taskTypeId"
          options={notSelectedTaskTypeOptions}
          value={form.taskType}
          onChange={onAddTaskType}
        />
      )}

      <Table sx={tableStyles}>
        <TableHead>
          <TableRow>
            <TableCell>Task Name</TableCell>
            <TableCell>SLA Group</TableCell>
            <TableCell align="right">
              <Tooltip PopperProps={disablePortalProp} title="Add task type">
                <IconButton
                  color="primary"
                  data-testid={ConditionsTestIds.CONDITION_FORM_ADD_TASK_TYPE_BUTTON}
                  size="small"
                  onClick={onAddTaskTypeButton}
                >
                  <AddBoxIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {formattedTasks.map((task, index) => {
            const conditionTaskType = form.conditionTaskTypes?.[task.taskTypeId];

            return (
              <React.Fragment key={task.taskTypeId}>
                <TableRow>
                  <TableCell>
                    <Tooltip
                      PopperProps={disablePortalProp}
                      className="max-w-160"
                      title={task.name}
                    >
                      <div className="truncate">{task.name}</div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      PopperProps={disablePortalProp}
                      className="max-w-160"
                      title={slaGroups[task.slaGroupId]?.name || 'N/A'}
                    >
                      <div className="truncate">{slaGroups[task.slaGroupId]?.name || 'N/A'}</div>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" className="flex gap-8">
                    {isBrokerType && isBrokerTasksEnabled && (
                      <Tooltip
                        PopperProps={disablePortalProp}
                        title={task.isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <IconButton
                          data-id={task.taskTypeId}
                          data-testid={ConditionsTestIds.CONDITION_FORM_TASK_COLLAPSE_EXPAND_BUTTON}
                          name="isPreApprovalExpanded"
                          size="small"
                          onClick={onExpandButton}
                        >
                          <ArrowIcon direction={task.isExpanded ? 'up' : 'down'} />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip PopperProps={disablePortalProp} title="Remove Task Type">
                      <IconButton
                        data-id={task.taskTypeId}
                        data-testid={ConditionsTestIds.CONDITION_FORM_REMOVE_TASK_TYPE_BUTTON}
                        disabled={isLoading}
                        size="small"
                        onClick={onDeleteTaskType}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                {isBrokerType && isBrokerTasksEnabled && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Collapse unmountOnExit in={task.isExpanded} timeout="auto">
                        <AddConditionFormTaskType
                          formName={formName}
                          handleChange={handleChange}
                          isBrokerType={isBrokerType}
                          key={task.taskTypeId}
                          setInForm={setInForm}
                          taskIndex={index}
                          taskType={conditionTaskType}
                          taskTypeDynamicFieldOptions={taskTypeDynamicFieldOptions}
                          validateField={validateField}
                          validationErrors={validationErrors}
                          onCreateNewForm={onCreateNewForm}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Stack>
  );
};

ConditionTasksTable.propTypes = {
  ...WithLoadingPropTypes,
  tasks: PropTypes.array.isRequired,
  taskTypeOptions: PropTypes.array.isRequired,
  form: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  formName: PropTypes.string,
  isTaskAssignmentEnabled: PropTypes.bool,
};

export default compose(withLoading, memo)(ConditionTasksTable);
